import { ufs } from "utils";
import { ERROR_MESSAGES } from "constants/error-messages";

export function hasError({ formData, inputError, name }) {
    let error;
    if (name) {
        error = inputError[name];
    } else {
        error = Object.values(inputError).some(value => value != undefined); // Safety measure for callbacks returning undefined/null
    }
    if (error) return true;

    for (const key in formData) {
        const formItem = formData[key];
        if (formItem.validations?.length) {
            for (const validation of formItem.validations) {
                if (validation.required && !formItem.value) {
                    return true;
                }

                if (validation.callback && validation.callback({ value: formItem.value, values: formData })) {
                    return true;
                }
            }
        }
    }

    return error;
}

export function checkErrors({ formData, inputError, values, name }) {
    const data = values || formData;
    let errors = inputError;

    if (name) {
        const formItem = data[name];
        if (formItem.validations?.length) {
            for (const validation of formItem.validations) {
                if (validation.required) {
                    if (!formItem.value || Array.isArray(formItem.value) && formItem.value.length === 0) {
                        errors[name] = ERROR_MESSAGES.required;
                        break;
                    } else {
                        delete errors[name];
                    }
                }

                if (validation.callback) {
                    const errorMessage = validation.callback({ value: formItem.value, values: data });
                    if (errorMessage) {
                        errors[name] = errorMessage;
                        break;
                    } else {
                        delete errors[name];
                    }
                }
            }
        }

        return errors;
    }

    Object.keys(data).forEach(key => {
        errors = checkErrors({formData: data, inputError: errors, name: key });
    });

    return errors;
}

export function validateAitNumber(valueArg, labelKey) {
    const value = valueArg.value ?? valueArg;
    if (!value) return;
    const pattern = /^[a-zA-Z]{2}[a-zA-Z0-9][0-9]{7}$/;

    if (!pattern.test(value)) {
        return `$t(system.messages.errors.invalid-format.masked.masculine, {"labelKey": "${labelKey}", "mask": "AAX9999999"})`;
    }
}

export function getMessageRecentViolation(violation, violationDetails) {
    return `Um auto de infração é permitido para esse veículo. Porém, já existe um auto de infração para ele. Tipo da infração: ${violation.infracao}. Data da infração: ${violation.data_infracao} Logradouro: ${violationDetails.logradouro_local_infracao}`;
}

export const getRecentViolationId = (violations, threshold = (7200000)) => {
    const now = new Date();
    const limit = new Date(now.getTime() - threshold);

    function parseDate(dateStr) {
        const [day, month, yearTime] = dateStr.split('/');
        const [year, hourMin] = yearTime.split(' ');
        const [hour, min] = hourMin.split(':');
        return new Date(year, month - 1, day, hour, min);
    }

    const recentViolations = violations.filter(violation => {
        const violationDate = parseDate(violation.data_infracao);
        return violationDate >= limit && violationDate <= now;
    });

    return recentViolations.length > 0 ? recentViolations[0] : null;
}

export function validateProcessNumber(valueArg) {
  const value = valueArg.value ?? valueArg;
  if (!value) return;
  const pattern = /^[a-zA-Z]{2}[0-9]{6}\/[0-9]{4}$/;

  if (!pattern.test(value)) {
    return `$t(process.messages.errors.process-number-format)`;
  }
}

export function validateUf(valueArg) {
    const value = valueArg.value ?? valueArg;
    if (!ufs.some(e => e.value === value.toUpperCase())) {
        return '$t(system.messages.errors.invalid-format.feminine, {"labelKey": "system.labels.uf"})';
    }
}

export function validateVehiclePlate(valueArg, labelKey) {
    const value = valueArg.value ?? valueArg;
    if (!value) return;
    const pattern = RegExp(/^[a-zA-Z]{3}[0-9][a-zA-Z0-9][0-9]{2}$/, "i");

    if (!pattern.test(value)) {
        return `$t(system.messages.errors.invalid-format.masked.feminine, {"labelKey": "${labelKey}", "mask": "AAA9X99"})`;
    }
}

export function validateYearFormat(value) {
    return !/^\d{4}$/.test(value);
}

export function isValidAitNumber(valueArg) {
    const value = valueArg.value ?? valueArg;
    const pattern = /^[a-zA-Z]{2}[a-zA-Z0-9][0-9]{7}$/;
    return pattern.test(value);
}

export function isValidVehiclePlate(valueArg) {
    const value = valueArg.value ?? valueArg;
    const pattern = RegExp(/^[a-zA-Z]{3}[0-9][a-zA-Z0-9][0-9]{2}$/, "i");
    return pattern.test(value);
}

export const validatePassword = ({ newPassword, confirmPassword }) => {
    const errors = {};
    const errorPath = "settings.user.change-password.messages.error";

    if (newPassword) {
        const rules = [
            { regex: /.{8,}/, message: `${errorPath}.min-length` },
            { regex: /[A-Z]/, message: `${errorPath}.missing-uppercase` },
            { regex: /[a-z]/, message: `${errorPath}.missing-lowercase` },
            { regex: /[0-9]/, message: `${errorPath}.missing-number` },
            { regex: /[^A-Za-z0-9]/, message: `${errorPath}.missing-special` },
        ];

        const failedRule = rules.find(rule => !rule.regex.test(newPassword));
        errors.newPassword = failedRule ? failedRule.message : null;
    }

    if (confirmPassword) {
        if (newPassword !== confirmPassword) {
            errors.confirmPassword = `${errorPath}.mismatch`;
        } else {
            errors.confirmPassword = null;
        }
    }

    return errors;
};

export const isValidCEP = (cep) => /^[0-9]{5}-?[0-9]{3}$/.test(cep);

export const maskCPF = (value) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

export const aitMask = (value = "") => {
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const [a = "", b = "", c = "", ...rest] = cleaned;

  return (
    ( /^[A-Z]$/.test(a) ? a : "" ) +
    ( /^[A-Z]$/.test(b) ? b : "" ) +
    ( /^[A-Z0-9]$/.test(c) ? c : "" ) +
    rest.join("").replace(/[^0-9]/g, "")
  ).slice(0, 10);
};

/**
 * Normaliza o valor do filtro de Núm. AIT: maiúsculas, apenas letras e números, máx. 10 caracteres.
 * Usado no filtro de infrações onde não se exige formato específico.
 */
export const aitFilterMask = (value = "") =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);

export const plateMask = (value = "") => {
  const match = value.toUpperCase().replace(/[^A-Z0-9]/g, "").match(/^([A-Z]{0,3})([0-9]?)([A-Z0-9]?)([0-9]{0,2})/);
  if (!match) return "";
  return match.slice(1).join("").slice(0, 7);
};

export const processNumberMask = (value = "") => {
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const match = cleaned.match(/^([A-Z]{0,2})([0-9]{0,6})([0-9]{0,4})/);

  if (!match) return "";

  const [, letters = "", firstDigits = "", lastDigits = ""] = match;

  const part1 = letters + firstDigits;
  const part2 = lastDigits;

  return part2 ? `${part1}/${part2}` : part1;
};

/**
 * Normaliza o valor do filtro de número de processo: maiúsculas, letras, números, "/" e "-", máx. 13 caracteres.
 * Usado no filtro de processos onde não se exige formato específico.
 */
export const processNumberFilterMask = (value = "") =>
  value.toUpperCase().replace(/[^A-Z0-9/\-]/g, "").slice(0, 13);

export const isOnlyLetters = (value) => (/^[a-zA-Z]*$/.test(value));

export const formatNotificationNumber = (value) => {
    return value
    .replace(/\D/g, '')
    .replace(/^(\d{5})(\d{1,4})?/, '$1/$2')
    .replace(/\/$/, '');
}

export const isValidNotificationNumber = (value) => {
    const regex = /^\d{5}\/\d{4}$/;
    return regex.test(value);
}  

export const parseStringToDateTime = (str) => {
    if (!str) return new Date(0);
    const [datePart, timePart] = str.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour = '00', minute = '00'] = timePart?.split(':') || [];
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
};

export const parseApiDateTime = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (/Z|[+-]\d{2}:\d{2}$/.test(value)) return new Date(value);

    const [datePart, timePart] = value.split('T');
    if (!timePart) return new Date(value);
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm, ss] = timePart.split(':').map(Number);
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0, 0);
}
